import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Link, Typography, Breadcrumbs, Button } from '@material-ui/core';
import { breadCrumbList, semanticValues, BASEPATH, LEAD_MANAGEMENT } from '../constant';
import { isEmpty, keysIn, get, findIndex } from 'lodash';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import I18n from '../../../config/locales';
import CheckPageAccess from '../../CheckPageAccess';
import clsx from 'clsx';
import queryString from 'query-string';
import { getEnlightenOrigin, isEnlm } from '../../../utils/helper';

const findAccountList = (breadCrumbList, pathName) => {
  return breadCrumbList.find(list => {
    return list.label === pathName;
  });
};

const replaceSemanticValues = (semanticUrl, props) => {
  let url = semanticUrl;
  semanticValues.forEach(semantic => {
    while (url.includes(`:${semantic.label}`)) {
      url = url.replace(`:${semantic.label}`, props[semantic.key]);
    }
  });
  return url;
};

const getBreadCrumbLink = (isHardReload, name, url, forwardTo) => {
  let modifiedUrl = url;
  // removing siteId from param when user clicks on breadcrumb and go to corresponding page
  if (name === 'Applications' || name === 'Incentive Programs' || (name === 'Dashboard' && url.includes('siteId'))) {
    modifiedUrl = url.split('?')[0];
  }
  // in sgip screen breadcrumb to change from applications to SGIP Applications
  let modifiedName = name;
  if (name === 'Applications') {
    modifiedName = 'SGIP Applications';
  }
  const props = isHardReload ? { href: modifiedUrl } : { onClick: () => forwardTo(modifiedUrl) };
  return (
    <Link color="textPrimary" key={modifiedUrl} {...props} style={{ cursor: 'pointer' }}>
      {modifiedName}
    </Link>
  );
};

const useStyles = makeStyles(theme => ({
  breadcrumb: {
    padding: theme.spacing(5, 4.5, 1)
  },
  backButton: {
    fontSize: 16,
    display: 'flex',
    alignItems: 'center',
    textTransform: 'initial',
    '&:hover': {
      textDecoration: 'underline',
      backgroundColor: theme.palette.secondary.main
    }
  },
  backArrow: {
    width: theme.spacing(2.5),
    height: theme.spacing(2.5)
  },
  paddingLeft: {
    paddingLeft: theme.spacing(3.5),
    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(2.5)
    }
  },
  link: {
    cursor: 'pointer'
  }
}));

const Breadcrumb = props => {
  const classes = useStyles();
  const {
    companyInfo,
    companyId,
    einOptIn,
    accountData,
    viewOnlyAccess,
    countryCode,
    history,
    showPage,
    setShowHidePage,
    forwardTo,
    isInstaller,
    isDRS
  } = props;
  const labelList = [];
  const childrenLabelList = {};
  let breadcrumbList = [];
  let matchProps = '';
  const fullPathName = props.matchProps.location.pathname;
  const breadcrumbPath = fullPathName.includes('dashboard') || (isDRS && !isInstaller) ? 'dashboard' : 'account';
  const basePathLabel = BASEPATH.map(path => path.label);
  const basePathGoBack = [];
  BASEPATH.forEach(path => path.hasGoBack && basePathGoBack.push(path));
  const goBackLabels = basePathGoBack.map(path => path.label);
  const leadsLabel = LEAD_MANAGEMENT.map(path => path.label);
  let hideBreadcrumb = false;
  const searchParam = get(props.matchProps, 'location.search', window.location.search) || window.location.search || '';
  const hoWithDrsAccess =
    get(accountData, 'company_id', null) === null &&
    get(get(accountData, 'service_roles', {}), 'drs_service_role', 'no_access') !== 'no_access';

  if (fullPathName.includes('/manager/')) {
    matchProps = props.matchProps.location.pathname.replace('/manager/', '');
  }

  let bList = matchProps;
  let matchPropsSplit = matchProps.split('/');
  const propsLastValue = matchPropsSplit[matchPropsSplit.length - 1];

  breadCrumbList.forEach(list => {
    const { label = '', hasChildren = false, childrenLabel = [] } = list;
    if (!isEmpty(label)) {
      labelList.push(label);
    }
    if (!basePathLabel.includes(label) && hasChildren) {
      childrenLabelList[label] = childrenLabel;
    }
  });
  labelList.forEach(label => {
    if (matchProps.includes(label)) {
      breadcrumbList.push(label);
    }
  });
  const activeKey = keysIn(childrenLabelList).find(key => {
    return breadcrumbList.includes(key) && propsLastValue !== key;
  });
  if (activeKey) {
    const activeList = childrenLabelList[activeKey];
    const findChildren = findIndex(activeList, child => child.includes(propsLastValue));
    const activeBreadcrumb = findChildren >= 0 || findChildren === 'new' ? activeList[findChildren] : activeList[0];
    if (!breadcrumbList.includes(activeBreadcrumb)) {
      breadcrumbList.push(activeBreadcrumb);
    }
    bList = breadcrumbList.join('/');
    breadcrumbList = [];
  }
  let modifiedMatchProps = bList;
  const isBreadcrumbListEmpty = isEmpty(breadcrumbList);

  // Lead Details START
  const isLead = leadsLabel.find(label => {
    if (isBreadcrumbListEmpty) {
      return bList.includes(label);
    } else {
      return breadcrumbList.includes(label);
    }
  });
  if (isLead) {
    const url = isBreadcrumbListEmpty ? bList : breadcrumbList.join('/');
    modifiedMatchProps = url.replace('company', breadcrumbPath);
  }
  // Lead Details END

  let pathNames = modifiedMatchProps.split('/');
  let lastIndex = pathNames.length - 1;

  if (isDRS && !isInstaller && isEmpty(pathNames[lastIndex])) {
    pathNames = [ 'design-review', 'application-status' ];
    lastIndex = pathNames.length - 1;
  } else if (hoWithDrsAccess) {
    // removing breadcrumbs in listing page for HO having drs access
    pathNames = [];
    lastIndex = pathNames.length - 1;
  }

  if (isDRS && isInstaller && pathNames[lastIndex] === 'application-status') {
    pathNames.splice(lastIndex, 1, 'edit-drs');
  }

  if (isEmpty(pathNames) || basePathLabel.includes(pathNames[lastIndex]) || pathNames[lastIndex] === '') {
    if (goBackLabels.includes(pathNames[lastIndex])) {
      return (
        <Fragment>
          <CheckPageAccess
            pageName={pathNames[lastIndex]}
            companyInfo={companyInfo}
            companyId={companyId}
            einOptIn={einOptIn}
            accountData={accountData}
            viewOnlyAccess={viewOnlyAccess}
            countryCode={countryCode}
            history={history}
            showPage={showPage}
            setShowHidePage={setShowHidePage}
            forwardTo={forwardTo}
          />
          {!props.fromITK && (
            <Typography component="div" className={clsx(classes.breadcrumb, classes.paddingLeft)}>
              <Button className={classes.backButton} color="textPrimary" onClick={() => history.goBack()}>
                <ArrowBackIosIcon className={classes.backArrow} />
                {I18n.t('buttons.back')}
              </Button>
            </Typography>
          )}
        </Fragment>
      );
    }
    hideBreadcrumb = true;
  }
  const hasHardLoad = pathNames.find(path => {
    const list = findAccountList(breadCrumbList, path);
    let hardReload = list && list.isHardload;
    if (isDRS && list && list.label === 'dashboard') {
      hardReload = true;
    }
    return hardReload;
  });
  const accountDetails = findAccountList(breadCrumbList, pathNames[lastIndex]);
  if (get(accountDetails, 'hideBreadcrumb', false)) {
    hideBreadcrumb = true;
  }

  const sgipChangeBreadcrumb =
    props.matchProps.location.pathname === '/manager/account/sgip-incentive/applications' ||
    props.matchProps.location.pathname === '/manager/dashboard/sgip-incentive/applications';
  const sgipActiveBreadcrumb = !isEmpty(props.matchProps.location.search) && sgipChangeBreadcrumb;

  const commercialBreadcrumb = props.matchProps.location.pathname === '/manager/dashboard/commercial';
  return (
    <Fragment>
      <CheckPageAccess
        pageName={pathNames[lastIndex]}
        companyInfo={companyInfo}
        companyId={companyId}
        einOptIn={einOptIn}
        accountData={accountData}
        viewOnlyAccess={viewOnlyAccess}
        countryCode={countryCode}
        history={history}
        showPage={showPage}
        setShowHidePage={setShowHidePage}
        forwardTo={forwardTo}
      />
      {!hideBreadcrumb &&
      !props.fromITK && (
        <Breadcrumbs aria-label="breadcrumb" className={classes.breadcrumb}>
          {pathNames.map((value, index) => {
            const account = findAccountList(breadCrumbList, value);
            if (account && !isEmpty(account)) {
              let url = account.isBasePath
                ? isEnlm() ? account.url : `${getEnlightenOrigin()}${account.url}`
                : `/manager/${breadcrumbPath}/${account.url}`;
              url = replaceSemanticValues(url, props);
              url = `${url}${searchParam}`;
              const breadcrumbName = I18n.t(`accountInfo.${account.name}`);
              const breadCrumbLink = getBreadCrumbLink(!!hasHardLoad, breadcrumbName, url, forwardTo);
              // in case of sgip application we have to keep the last breadcrumb active
              let breadcrumbValue =
                (index === lastIndex && !sgipActiveBreadcrumb) || account.label === 'admin'
                  ? breadcrumbName
                  : breadCrumbLink;
              // in sgip screen breadcrumb to change from applications to SGIP Applications
              if (index === lastIndex && sgipChangeBreadcrumb && !sgipActiveBreadcrumb) {
                breadcrumbValue = 'SGIP Applications';
              } else if (index === lastIndex && commercialBreadcrumb) {
                breadcrumbValue = 'Commercial';
              } else if (index === lastIndex && account.name === 'my_leads') {
                const { from = '' } = queryString.parse(get(props, 'matchProps.history.location.search', ''));
                if (from === 'bom-dialog') {
                  breadcrumbValue = 'BOM Calculator';
                }
              }
              return <Typography key={`breadcrumb_${index}`}>{breadcrumbValue}</Typography>;
            }
            return <Typography key={`breadcrumb_${index}`}>{value}</Typography>;
          })}
        </Breadcrumbs>
      )}
    </Fragment>
  );
};

Breadcrumb.propTypes = {
  companyInfo: PropTypes.object,
  companyId: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
  einOptIn: PropTypes.bool,
  accountData: PropTypes.object,
  viewOnlyAccess: PropTypes.bool,
  countryCode: PropTypes.string,
  forwardTo: PropTypes.func,
  matchProps: PropTypes.object,
  showPage: PropTypes.bool,
  setShowHidePage: PropTypes.func
};

export default Breadcrumb;
